import React from 'react';
import { CheckCircle, X, Minus } from 'lucide-react';

const ROWS = [
  { label: 'Turnaround Time', solicitor: '5-10 business days', diy: 'Hours of research', rentfix: 'Under 5 minutes' },
  { label: 'Cost', solicitor: '\u00A3500 - \u00A32,000+', diy: 'Free but risky', rentfix: '\u00A315.50' },
  { label: 'RRB 2025 Coverage', solicitor: 'Varies by firm', diy: 'Unlikely up-to-date', rentfix: '100% integrated' },
  { label: 'Rewritten Draft', solicitor: 'Extra charge', diy: 'Not included', rentfix: 'Included' },
  { label: 'Clause-by-Clause', solicitor: 'Varies', diy: 'Manual review', rentfix: 'Every clause analysed' },
  { label: 'Compliance Score', solicitor: 'Not provided', diy: 'Not provided', rentfix: 'Detailed score report' },
];

function CellValue({ value, isRentfix }: { value: string; isRentfix: boolean }) {
  if (isRentfix) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900">
        <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
        {value}
      </span>
    );
  }

  const isNegative = value.includes('Not') || value.includes('risky') || value.includes('Unlikely') || value.includes('Extra');
  if (isNegative) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-gray-400">
        <X className="w-3.5 h-3.5 flex-shrink-0" />
        {value}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
      <Minus className="w-3.5 h-3.5 flex-shrink-0" />
      {value}
    </span>
  );
}

export function ComparisonTable() {
  return (
    <section className="py-20 sm:py-28 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">How We Compare</h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            See how our AI-powered review stacks up against traditional alternatives.
          </p>
        </div>

        <div className="hidden md:block animate-on-scroll">
          <div className="rounded-2xl border border-gray-200 overflow-hidden shadow-elevated">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-400 w-1/4" />
                  <th className="text-center py-4 px-4 text-sm font-medium text-gray-500 w-1/4">
                    Traditional Solicitor
                  </th>
                  <th className="text-center py-4 px-4 text-sm font-medium text-gray-500 w-1/4">
                    DIY Review
                  </th>
                  <th className="text-center py-4 px-4 w-1/4 bg-gradient-to-r from-emerald-600 to-teal-500 text-white rounded-t-none">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-white/80">Recommended</span>
                      <span className="text-sm font-semibold">RentFix &mdash; &pound;15.50</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row, i) => (
                  <tr key={row.label} className={i < ROWS.length - 1 ? 'border-b border-gray-100' : ''}>
                    <td className="py-4 px-6 text-sm font-medium text-gray-700">{row.label}</td>
                    <td className="py-4 px-4 text-center">
                      <CellValue value={row.solicitor} isRentfix={false} />
                    </td>
                    <td className="py-4 px-4 text-center">
                      <CellValue value={row.diy} isRentfix={false} />
                    </td>
                    <td className="py-4 px-4 text-center bg-gray-50">
                      <CellValue value={row.rentfix} isRentfix={true} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="md:hidden space-y-4">
          {ROWS.map((row) => (
            <div key={row.label} className="animate-on-scroll bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{row.label}</p>
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Solicitor</span>
                  <CellValue value={row.solicitor} isRentfix={false} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">DIY</span>
                  <CellValue value={row.diy} isRentfix={false} />
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <span className="text-xs font-semibold text-gray-900">RentFix</span>
                  <CellValue value={row.rentfix} isRentfix={true} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
